import axios from 'axios'
import router from '@/router'
import { BASE_URL } from '@/config'
import { ElMessage } from 'element-plus'

// 雪花算法ID精度保护：JSON.parse 前将「JSON 值位置」的 16 位以上整数转为字符串。
// 正则必须限定上下文：前面是 "键名": 、后面紧跟 , } ]，
// 否则会误伤字符串内部的大数字（如 "李向阳（id:2084833890154016769）"）导致 JSON 被破坏、解析失败。
function safeJsonParse(text) {
  const safeText = text.replace(/("(?:[^"\\]|\\.)*"\s*:\s*)(-?\d{16,})(?=\s*[,}\]])/g, '$1"$2"')
  return JSON.parse(safeText)
}

const request = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  transformResponse: [function (data) {
    try {
      return safeJsonParse(data)
    } catch (e) {
      return data
    }
  }],
})

let isRedirectingToLogin = false

function redirectToLogin() {
  if (isRedirectingToLogin) return
  isRedirectingToLogin = true

  ElMessage.error('登录已过期，请重新登录')

  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('qek_auto_login')
  localStorage.removeItem('qek_refresh_token')

  router.replace('/login')
  setTimeout(() => { isRedirectingToLogin = false }, 3000)
}

// 单飞刷新：避免并发请求同时触发多次 refresh
let refreshing = null

/**
 * 静默刷新 access token。
 * 返回结果对象，必须区分三种情况（2026-09-10 排查结论）：
 * - { status: 'ok', token }  刷新成功
 * - { status: 'network' }    网络中断/网关故障——凭证未必失效，不能踢人下线
 * - { status: 'invalid' }    后端明确拒绝（刷新令牌无效或已过期）——需要重新登录
 */
async function refreshAccessToken() {
  if (refreshing) return refreshing
  refreshing = (async () => {
    const rt = localStorage.getItem('qek_refresh_token')
    if (!rt) return { status: 'invalid' }
    let res
    try {
      const { refreshToken } = await import('@/api/auth')
      res = await refreshToken({ refreshToken: rt })
    } catch (e) {
      // 后端业务层明确拒绝（刷新令牌无效/已过期）→ 凭证确实失效
      if (e && e._isBizError) return { status: 'invalid' }
      // 网络中断、网关 5xx、请求超时等 → 按网络问题处理，保留本地凭证
      return { status: 'network', error: e }
    }
    if (res && res.data && res.data.token) {
      localStorage.setItem('token', res.data.token)
      if (res.data.refreshToken) {
        localStorage.setItem('qek_refresh_token', res.data.refreshToken)
      }
      return { status: 'ok', token: res.data.token }
    }
    return { status: 'invalid' }
  })().finally(() => { refreshing = null })
  return refreshing
}

function authError() {
  const err = new Error()
  err._isAuthError = true
  return err
}

request.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  (error) => Promise.reject(error)
)

request.interceptors.response.use(
  (response) => {
    const body = response.data
    const url = response.config?.url || ''
    if (body && body.code !== undefined && body.code === 502 && !url.includes('/auth/refresh')) {
      return tryRefreshAndRetry(errorFromBody(body), response.config)
    }
    if (body && body.code !== undefined && body.code !== 0) {
      // 标记「后端业务层明确拒绝」：refresh 流程据此判定凭证确已失效（而非网络问题），必须打标
      const bizErr = new Error(body.msg || '业务异常')
      bizErr._isBizError = true
      return Promise.reject(bizErr)
    }
    return body
  },
  (error) => {
    const status = error.response?.status
    const url = error.config?.url || ''
    const body = error.response?.data
    // refresh 接口自身失败时直接拒绝，避免递归死锁。
    // 若能拿到本系统的 JSON 响应体（含 code 字段）说明后端明确拒绝 → 标记为业务拒绝；
    // 否则（无响应/网关 HTML/超时）保持为网络问题，交由 refreshAccessToken 判定
    if (url.includes('/auth/refresh')) {
      if (body && typeof body === 'object' && body.code !== undefined) {
        error._isBizError = true
      }
      return Promise.reject(error)
    }
    // 只有「登录过期/未认证」才尝试静默续期：
    // - 401：明确的未认证状态码
    // - 502：仅当响应体是本系统返回的认证失败结构（{code:502/401}）时才算登录超时；
    //   nginx 等网关自身的 502 返回 HTML/纯文本，必须按「服务不可用」处理，
    //   否则会把用户误踢下线并清空本地凭证（2026-09-10 排查结论）
    if (status === 401 || (status === 502 && isAuthFailure(body))) {
      return tryRefreshAndRetry(error, error.config)
    }
    // 网关/服务端不可用：属于网络问题，不代表登录失效，提示后保留本地凭证
    // 500 一并纳入：后端未捕获异常时若直出 axios 英文文案（Request failed with status code 500），用户无法理解
    if (status === 500 || status === 502 || status === 503 || status === 504) {
      notifyGatewayUnavailable()
      return Promise.reject(networkError(error))
    }
    // 403：后端 @RequirePermission 接口权限不足（用户已登录但无该操作权限）
    if (status === 403) {
      const msg = error.response?.data?.msg || '您没有该操作权限'
      ElMessage.warning(msg)
      return Promise.reject(new Error(msg))
    }
    if (!error.response) {
      console.warn('网络连接失败，请检查后端服务是否启动')
      return Promise.reject(networkError(new Error('网络连接失败')))
    }
    return Promise.reject(error)
  }
)

// 业务码 502 没有真实 axios error，构造一个带 response 的错误以便复用重试逻辑
function errorFromBody(body) {
  const err = new Error('login timeout')
  err.response = { status: 502, data: body }
  return err
}

// 判断响应体是否为「后端本系统返回的认证失败」。
// 后端 TokenAuthenticationFilter 拦截时返回 HTTP 502/401 且响应体是 {code:502,msg:'Unauthorized'}；
// 而 nginx 等网关层的 502 返回的是 HTML 或纯文本。二者必须区分：
// 否则网关故障会被当成「登录过期」，把用户踢下线并清空本地凭证（2026-09-10 排查结论）。
function isAuthFailure(body) {
  return !!body && typeof body === 'object' && (body.code === 502 || body.code === 401)
}

// 标记「网络/网关故障」类错误：已提示用户重试，不应触发跳登录页
function networkError(error) {
  const err = error instanceof Error ? error : new Error('服务暂时不可用，请稍后重试')
  err._isNetworkError = true
  return err
}

// 服务不可用提示节流：页面初始化时并发请求会同时失败，避免弹窗风暴
const GATEWAY_NOTICE_INTERVAL = 3000
let lastGatewayNoticeAt = 0
function notifyGatewayUnavailable() {
  const now = Date.now()
  if (now - lastGatewayNoticeAt < GATEWAY_NOTICE_INTERVAL) return
  lastGatewayNoticeAt = now
  ElMessage.error('服务暂时不可用，请稍后重试')
}

// 收到认证失败（业务码 502/401 或 HTTP 401）：尝试用 refreshToken 静默刷新 access token 并重试原请求一次。
// 只有「后端明确拒绝刷新」才跳登录页；网络故障、或刷新成功但重试仍失败，都不能误判为登录过期
// （旧实现用 .catch() 兜住整条链，导致刷新成功后的重试请求一旦报错也会被登出 —— 2026-09-10 排查结论）
function tryRefreshAndRetry(originalError, config) {
  if (config && config._retried) {
    // 已带新 token 重试过一次仍认证失败 → 凭证确实失效
    redirectToLogin()
    return Promise.reject(authError())
  }
  const rt = localStorage.getItem('qek_refresh_token')
  if (!rt || !config) {
    redirectToLogin()
    return Promise.reject(authError())
  }
  return refreshAccessToken().then((result) => {
    if (result.status === 'network') {
      // 网络中断导致刷新没走通：凭证未必失效，提示重试但不踢人下线
      notifyGatewayUnavailable()
      return Promise.reject(networkError(result.error))
    }
    if (result.status !== 'ok' || !result.token) {
      // 后端明确拒绝（refresh token 无效/已过期）→ 需要重新登录
      redirectToLogin()
      return Promise.reject(authError())
    }
    config.headers = config.headers || {}
    config.headers['Authorization'] = `Bearer ${result.token}`
    config._retried = true
    return request(config)
  })
}

export default request
